import React, { useEffect } from 'react'
import { useAuth } from 'wasp/client/auth'
import { useNavigate } from 'react-router-dom'
import { useAction, useQuery } from 'wasp/client/operations'
// AppFrame removed: route-level layout wraps admin pages
import { Card } from '../../components/ui/card'
import { getAdminMetrics, getAdminAlerts, getCacheStats, runDbBackupOnce, getTasteProfileStatusAdmin } from 'wasp/client/operations'

export function AdminSystem() {
  const { data: user } = useAuth()
  const navigate = useNavigate()
  const isAdmin = Boolean((user as any)?.isAdmin)
  useEffect(() => { if (user && !isAdmin) navigate('/') }, [user, isAdmin])
  const { data, isLoading, error } = useQuery(getAdminMetrics, { preset: 'system' } as any, { enabled: isAdmin })
  const { data: alerts } = useQuery(getAdminAlerts, undefined, { enabled: isAdmin })
  const { data: cache } = useQuery(getCacheStats, undefined, { enabled: isAdmin })
  const { data: taste } = useQuery(getTasteProfileStatusAdmin as any, undefined, { enabled: isAdmin })
  const tp: any = taste || {}
  const doBackup = useAction(runDbBackupOnce)
  if ((error as any)?.message?.includes('403')) {
    return <div className="p-6 text-red-600">Access denied: admin only.</div>
  }
  if (isLoading) return (
      <div className="space-y-6">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-martinique dark:text-white">System Status</h1>
          <p className="text-martinique/70 dark:text-white/70 mt-2">Loading system metrics and alerts...</p>
        </div>
        <div className="animate-pulse space-y-6">
          <div className="h-32 bg-stone dark:bg-darkcard rounded-xl"></div>
          <div className="h-32 bg-stone dark:bg-darkcard rounded-xl"></div>
        </div>
      </div>
  )
  return (
    
      <div className="space-y-6">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-martinique dark:text-white">System Status</h1>
          <p className="text-martinique/70 dark:text-white/70 mt-2">System health, alerts, and administrative actions</p>
        </div>

        <Card className="p-8 bg-sand dark:bg-darkcard border-0 rounded-xl shadow-card">
          <div className="text-xl font-medium text-martinique dark:text-white mb-6">System Actions</div>
          <button
            onClick={async () => {
              try { await doBackup({}); alert('Backup started. Check alerts for status.')} catch { alert('Backup failed to start.') }
            }}
            className="px-4 py-2 bg-coral dark:bg-darkspice text-white rounded-lg hover:bg-coral/90 dark:hover:bg-darkspice/90 transition-colors"
          >
            Backup DB Now
          </button>
        </Card>
        {/* System Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <Card className="p-6 bg-sand dark:bg-darkcard border-0 rounded-xl shadow-card hover:shadow-cardHover transition-all duration-200">
            <div className="text-sm font-medium text-martinique/60 dark:text-white/60 mb-3">Alerts (recent)</div>
            <div className="text-3xl font-bold text-martinique dark:text-white">{Array.isArray(alerts) ? alerts.length : 0}</div>
          </Card>
          <Card className="p-6 bg-sand dark:bg-darkcard border-0 rounded-xl shadow-card hover:shadow-cardHover transition-all duration-200">
            <div className="text-sm font-medium text-martinique/60 dark:text-white/60 mb-3">Cache Size</div>
            <div className="text-3xl font-bold text-martinique dark:text-white">{cache?.totalRecipes ?? 0}</div>
          </Card>
          <Card className="p-6 bg-sand dark:bg-darkcard border-0 rounded-xl shadow-card hover:shadow-cardHover transition-all duration-200">
            <div className="text-sm font-medium text-martinique/60 dark:text-white/60 mb-3">Cache Uses (24h)</div>
            <div className="text-3xl font-bold text-martinique dark:text-white">{cache?.recentUses ?? 0}</div>
          </Card>
          <Card className="p-6 bg-sand dark:bg-darkcard border-0 rounded-xl shadow-card hover:shadow-cardHover transition-all duration-200">
            <div className="text-sm font-medium text-martinique/60 dark:text-white/60 mb-3">Hit Rate</div>
            <div className="text-3xl font-bold text-martinique dark:text-white">{cache?.hitRate ? `${Math.round(cache.hitRate * 100)}%` : '—'}</div>
          </Card>
        </div>

        {/* Taste Profile Build Status */}
        <Card className="p-8 bg-sand dark:bg-darkcard border-0 rounded-xl shadow-card">
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="text-xl font-medium text-martinique dark:text-white">Taste Profile Status</div>
              <div className="text-sm text-martinique/60 dark:text-white/60">Provider: {tp?.provider ?? '—'}</div>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
            <div className="rounded-lg bg-lynx dark:bg-martinique p-4">
              <div className="text-xs text-martinique/60 dark:text-white/60">All</div>
              <div className="text-2xl font-semibold">{tp?.totals?.all ?? 0}</div>
            </div>
            <div className="rounded-lg bg-lynx dark:bg-martinique p-4">
              <div className="text-xs text-martinique/60 dark:text-white/60">Ready</div>
              <div className="text-2xl font-semibold text-green-600">{tp?.totals?.ready ?? 0}</div>
            </div>
            <div className="rounded-lg bg-lynx dark:bg-martinique p-4">
              <div className="text-xs text-martinique/60 dark:text-white/60">Pending</div>
              <div className="text-2xl font-semibold text-amber-600">{tp?.totals?.pending ?? 0}</div>
            </div>
            <div className="rounded-lg bg-lynx dark:bg-martinique p-4">
              <div className="text-xs text-martinique/60 dark:text-white/60">Failed</div>
              <div className="text-2xl font-semibold text-red-600">{tp?.totals?.failed ?? 0}</div>
            </div>
            <div className="rounded-lg bg-lynx dark:bg-martinique p-4">
              <div className="text-xs text-martinique/60 dark:text-white/60">Idle</div>
              <div className="text-2xl font-semibold">{tp?.totals?.idle ?? 0}</div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <div className="text-sm font-medium text-martinique dark:text-white mb-2">Recent Failures</div>
              <div className="space-y-2">
                {(tp?.recentFailed ?? []).map((f: any) => (
                  <div key={`f-${f.userId}-${f.updatedAt}`} className="text-sm bg-lynx dark:bg-martinique rounded-md p-3">
                    <div className="font-mono text-xs">{f.userId}</div>
                    <div className="text-martinique/70 dark:text-white/70 text-xs">{new Date(f.updatedAt).toLocaleString()}</div>
                    {f.reason && <div className="text-red-600 text-xs mt-1">{f.reason}</div>}
                  </div>
                ))}
                {!(tp?.recentFailed ?? []).length && <div className="text-sm text-martinique/70 dark:text-white/70">None</div>}
              </div>
            </div>
            <div>
              <div className="text-sm font-medium text-martinique dark:text-white mb-2">Recent Pending</div>
              <div className="space-y-2">
                {(tp?.recentPending ?? []).map((p: any) => (
                  <div key={`p-${p.userId}-${p.updatedAt}`} className="text-sm bg-lynx dark:bg-martinique rounded-md p-3">
                    <div className="font-mono text-xs">{p.userId}</div>
                    <div className="text-martinique/70 dark:text-white/70 text-xs">{new Date(p.updatedAt).toLocaleString()}</div>
                  </div>
                ))}
                {!(tp?.recentPending ?? []).length && <div className="text-sm text-martinique/70 dark:text-white/70">None</div>}
              </div>
            </div>
          </div>
        </Card>
        {/* Recent Alerts */}
        <Card className="p-8 bg-sand dark:bg-darkcard border-0 rounded-xl shadow-card">
          <div className="text-xl font-medium text-martinique dark:text-white mb-6">Recent Alerts</div>
          {!alerts?.length && <div className="text-martinique/70 dark:text-white/70">No recent alerts.</div>}
          {!!alerts?.length && (
            <div className="space-y-3">
              {alerts.map((a: any) => (
                <div key={a.id} className="border border-white/5 rounded-lg p-6 bg-lynx dark:bg-martinique">
                  <div className="text-sm text-martinique/70 dark:text-white/70 mb-1">{new Date(a.createdAt).toLocaleString()}</div>
                  <div className="font-medium text-martinique dark:text-white">{a.subject || 'Alert'}</div>
                  {a.body && <div className="text-sm text-martinique/80 dark:text-white/80 mt-2 whitespace-pre-wrap">{a.body}</div>}
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Raw System Data */}
        <Card className="p-8 bg-sand dark:bg-darkcard border-0 rounded-xl shadow-card">
          <div className="text-xl font-medium text-martinique dark:text-white mb-6">System Data</div>
          <div className="bg-lynx dark:bg-martinique rounded-lg p-6">
            <pre className="text-sm text-martinique/80 dark:text-white/80 overflow-auto whitespace-pre-wrap font-mono">{JSON.stringify(data, null, 2)}</pre>
          </div>
        </Card>
      </div>
  )
}

export default AdminSystem
